import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';

const Applications = () => {
    const [drives, setDrives] = useState([]);
    const [selectedDrive, setSelectedDrive] = useState('');
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchDrives();
    }, []);

    useEffect(() => {
        if (selectedDrive) {
            fetchApplications();
        }
    }, [selectedDrive]);

    const fetchDrives = async () => {
        try {
            const res = await axios.get(`${baseApiURL()}/placement/drives/get`);
            if (res.data.success) {
                setDrives(res.data.drives);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch drives');
        }
    };

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${baseApiURL()}/placement/applications/drive/${selectedDrive}`);
            if (res.data.success) {
                setApplications(res.data.applications);
                console.log('Applications:', res.data.applications); // Debug log
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch applications');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (appId, newStatus) => {
        try {
            const res = await axios.put(`${baseApiURL()}/placement/applications/update/${appId}/status`, 
                { status: newStatus },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            if (res.data.success) {
                toast.success(res.data.message);
                fetchApplications(); // refresh
            }
        } catch (error) {
            toast.error('Error updating status');
        }
    };

    // Helper function to get full name
    const getFullName = (student) => {
        if (!student) return 'N/A';
        const parts = [student.firstName, student.middleName, student.lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'N/A';
    };

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                <div className="bento-header-banner flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Student Applications</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                                Review student applicants, inspect resumes, and manage hiring pipeline stages.
                            </p>
                        </div>
                    </div>

                    <div className="w-full sm:w-72">
                        <select 
                            value={selectedDrive} 
                            onChange={(e) => setSelectedDrive(e.target.value)}
                            className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
                        >
                            <option value="">-- Choose Placement Drive --</option>
                            {drives.map(drive => (
                                <option key={drive._id} value={drive._id}>
                                    {drive.title} - {drive.company?.name || drive.companyName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {!selectedDrive ? (
                        <div className="text-center py-12 text-slate-400 text-xs font-semibold">
                            Please select a placement drive from the dropdown above to view student applications.
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Summary Section */}
                            {applications.length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div className="p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total</span>
                                        <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{applications.length}</p>
                                    </div>
                                    <div className="p-3.5 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl">
                                        <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Applied</span>
                                        <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-0.5">
                                            {applications.filter(a => a.status === 'Applied').length}
                                        </p>
                                    </div>
                                    <div className="p-3.5 bg-amber-50/60 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800/60 rounded-xl">
                                        <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Shortlisted</span>
                                        <p className="text-xl font-bold text-amber-700 dark:text-amber-300 mt-0.5">
                                            {applications.filter(a => a.status === 'Shortlisted').length}
                                        </p>
                                    </div>
                                    <div className="p-3.5 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
                                        <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Selected</span>
                                        <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mt-0.5">
                                            {applications.filter(a => a.status === 'Selected').length}
                                        </p>
                                    </div>
                                    <div className="p-3.5 bg-rose-50/60 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-800/60 rounded-xl">
                                        <span className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Rejected</span>
                                        <p className="text-xl font-bold text-rose-700 dark:text-rose-300 mt-0.5">
                                            {applications.filter(a => a.status === 'Rejected').length}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                {loading ? (
                                    <div className="text-center py-12 text-slate-400 text-xs font-semibold">
                                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent"></div>
                                        <p className="mt-2">Loading applications...</p>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                                            <tr>
                                                <th className="py-3 px-4 text-left">Enrollment No</th>
                                                <th className="py-3 px-4 text-left">Student Name</th>
                                                <th className="py-3 px-4 text-left">Branch</th>
                                                <th className="py-3 px-4 text-left">Semester</th>
                                                <th className="py-3 px-4 text-left">Backlogs</th>
                                                <th className="py-3 px-4 text-left">Applied Date</th>
                                                <th className="py-3 px-4 text-left">Resume</th>
                                                <th className="py-3 px-4 text-left">Status</th>
                                                <th className="py-3 px-4 text-left">Update Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                                            {applications.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="py-8 text-center text-slate-400 text-xs font-semibold">
                                                        No applications found for this drive.
                                                    </td>
                                                </tr>
                                            ) : (
                                                applications.map(app => (
                                                    <tr key={app._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                                                        <td className="py-3 px-4 font-mono font-bold text-slate-900 dark:text-white">
                                                            {app.student?.enrollmentNo || 'N/A'}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <p className="font-bold text-slate-900 dark:text-white">{getFullName(app.student)}</p>
                                                            <p className="text-[11px] text-slate-400">{app.student?.email || 'N/A'}</p>
                                                        </td>
                                                        <td className="py-3 px-4 font-medium">
                                                            {app.student?.branch || 'N/A'}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            Sem {app.student?.semester || 'N/A'}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            {(() => {
                                                              const count = app.student?.activeBacklogs ?? null;
                                                              const details = app.student?.backlogDetails;
                                                              if (count === null || count === undefined) return <span className="text-slate-400 text-xs">—</span>;
                                                              return (
                                                                <span
                                                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                                    count === 0 ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' : 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                                                                  }`}
                                                                  title={details || ''}
                                                                >
                                                                  {count === 0 ? '✓ No Backlogs' : `⚠ ${count} Backlog${count > 1 ? 's' : ''}`}
                                                                </span>
                                                              );
                                                            })()}
                                                        </td>
                                                        <td className="py-3 px-4 text-slate-500 dark:text-slate-400">
                                                            {new Date(app.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            {app.student?.resumeLink ? (
                                                                <a 
                                                                    href={app.student.resumeLink} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900 border border-indigo-200 dark:border-indigo-800 transition shadow-xs"
                                                                >
                                                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"></path>
                                                                    </svg>
                                                                    View Resume
                                                                </a>
                                                            ) : (
                                                                <span className="text-slate-400 text-xs italic">Not Uploaded</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border
                                                                ${app.status === 'Applied' ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                                                                  app.status === 'Shortlisted' ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                                                                  app.status === 'Selected' ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' :
                                                                  app.status === 'Rejected' ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800' :
                                                                  app.status === 'Waitlisted' ? 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800' :
                                                                  'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                                                }`}
                                                            >
                                                                {app.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <select 
                                                                value={app.status} 
                                                                onChange={(e) => handleStatusUpdate(app._id, e.target.value)}
                                                                className="px-2.5 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
                                                            >
                                                                <option value="Applied">Applied</option>
                                                                <option value="Shortlisted">Shortlisted</option>
                                                                <option value="Selected">Selected</option>
                                                                <option value="Waitlisted">Waitlisted</option>
                                                                <option value="Rejected">Rejected</option>
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Applications;